import {inject, Injectable} from '@angular/core';
import {v4 as uuidv4} from 'uuid';
import {PlatformService} from './platform.service';
import {UserService} from './user.service';
import {AmplitudeEvent, TikTokPayload} from './analytics.service';
import {distinctUntilChanged, filter, map, take} from 'rxjs';

interface QueuedEvent {
  eventName: string;
  payload: TikTokPayload;
}

@Injectable({
  providedIn: 'root'
})
export class TikTokTrackingService {
  private readonly platformService = inject(PlatformService);
  private readonly userService = inject(UserService);

  private eventQueue: QueuedEvent[] = [];
  private isIdentified = false;

  initialize(): void {
    if (this.platformService.isServer) return;

    this.userService.user$.pipe(
      map(user => user.status !== 'unauthorized' ? user : undefined),
      filter(user => !!user),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      take(1),
    ).subscribe(async (user) => {
      await this.identifyUser(user.id);
      this.isIdentified = true;
      this.flushQueue();
    });
  }

  trackEvent(
    event: AmplitudeEvent,
    properties?: Record<string, string | number | undefined>,
  ): void {
    if (this.platformService.isServer) return;

    const eventName = this.mapToTikTokEventName(event);
    if (!eventName) return;

    const payload = this.buildPayload(eventName, properties);

    if (this.isIdentified) {
      this.sendToTikTok(eventName, payload);
    } else {
      this.eventQueue.push({eventName, payload});
    }
  }

  private flushQueue(): void {
    const events = this.eventQueue.splice(0);
    for (const {eventName, payload} of events) {
      this.sendToTikTok(eventName, payload);
    }
  }

  private mapToTikTokEventName(event: AmplitudeEvent): string | null {
    switch (event) {
      case 'signup-success':
        return 'CompleteRegistration';
      case 'payment-success':
        return 'Purchase';
      case 'payment-started':
      case 'resubscribe-payment-started':
        return 'InitiateCheckout';
      case 'benefits-page-viewed':
        return 'ViewContent';
      default:
        return null;
    }
  }

  private async identifyUser(userId: string): Promise<void> {
    const ttq = this.getTtq();
    if (!ttq || typeof ttq.identify !== 'function') return;

    const hashedId = await this.sha256(userId.trim());
    if (!hashedId) return;

    ttq.identify({external_id: hashedId});
  }

  private sendToTikTok(eventName: string, payload: TikTokPayload): void {
    const ttq = this.getTtq();
    if (!ttq || typeof ttq.track !== 'function') return;

    try {
      if (Object.keys(payload).length > 0) {
        ttq.track(eventName, payload);
      } else {
        ttq.track(eventName);
      }
    } catch (err) {
      console.error('TikTok pixel track error', err);
    }
  }

  private getTtq(): { track?: (event: string, payload?: unknown) => void; identify?: (payload: unknown) => void } | undefined {
    if (this.platformService.isServer) return undefined;
    return (window as unknown as { ttq?: { track?: (event: string, payload?: unknown) => void; identify?: (payload: unknown) => void } }).ttq;
  }

  private buildPayload(
    eventName: string,
    properties?: Record<string, string | number | undefined>,
  ): TikTokPayload {
    const payload: TikTokPayload = {};
    const defaultValue = 15;

    const value = typeof properties?.['amount'] === 'number'
      ? properties['amount']
      : typeof properties?.['value'] === 'number'
        ? properties['value']
        : defaultValue;

    if (value !== undefined) {
      payload.value = value;
      payload.currency = 'USD';
      payload.contents = [{
        content_id: 'homey_subscription',
        content_type: 'product',
        content_name: 'Homey subscription',
        quantity: 1,
        price: value,
      }];
    }

    const searchString = properties?.['search_string'] ?? properties?.['search'];
    if (typeof searchString === 'string') {
      payload.search_string = searchString;
    }

    const eventIdProp = properties?.['event_id'];
    payload.event_id = typeof eventIdProp === 'string' ? eventIdProp : uuidv4();

    payload.context = {
      page: {
        url: typeof location !== 'undefined' ? location.href : undefined,
      },
      user: {
        _ttp: this.getCookie('_ttp'),
      },
    };

    Object.keys(payload).forEach((key) => {
      const v = (payload as Record<string, unknown>)[key];
      if (v === undefined || v === null) {
        delete (payload as Record<string, unknown>)[key];
      }
    });

    return payload;
  }

  private async sha256(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private getCookie(name: string): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return match?.split('=')[1];
  }
}
